/**
 * /workspace/sell/payouts — weekly cash-out queue backed by db.payouts.
 */
import React from "react";
import { useQuery } from "@tanstack/react-query";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { api } from "@/lib/api";
import { IndianRupee, Clock, Wallet, TrendingUp, Loader2 } from "lucide-react";

interface Payout {
  payout_id: string;
  amount: number;
  currency: string;
  status: string;
  period_start: string;
  period_end: string;
  utr?: string | null;
  created_at: string;
  paid_at?: string | null;
  note?: string;
}

export default function SellPayouts() {
  const q = useQuery<{ items: Payout[]; kpi: any }>({
    queryKey: ["sell-payouts"],
    queryFn: async () => (await api.get(`/workspace/sell/payouts?limit=100`)).data,
  });
  const k = q.data?.kpi;

  return (
    <DashboardLayout title="Payouts" subtitle="Weekly cash-out queue — settled, in-flight and held. UTR references included.">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-5">
        <Kpi label="Total paid" value={inr(k?.total_paid)} icon={TrendingUp} tone="emerald"/>
        <Kpi label="Queued for next batch" value={inr(k?.queued)} icon={Clock} tone="amber"/>
        <Kpi label="Held (KYC pending)" value={inr(k?.held)} icon={Wallet} tone="rose"/>
      </div>

      <div className="rounded-2xl border border-[color:var(--sg-border)] bg-[color:var(--sg-card)] overflow-hidden">
        {q.isLoading ? (
          <div className="p-8 text-center text-[color:var(--sg-muted)]"><Loader2 className="h-4 w-4 inline animate-spin mr-2"/> Loading…</div>
        ) : (q.data?.items?.length ?? 0) === 0 ? (
          <div className="p-12 text-center text-[color:var(--sg-muted)]">No payouts yet. Your first cash-out lands next Friday.</div>
        ) : (
          <div className="overflow-x-auto sg-scrollbar-thin">
            <table className="w-full text-sm" data-testid="payouts-table">
              <thead>
                <tr className="text-[10px] uppercase tracking-widest text-[color:var(--sg-muted)] border-b border-[color:var(--sg-border)]">
                  <th className="text-left py-2 pl-4">Payout · Period</th>
                  <th className="text-right py-2">Amount</th>
                  <th className="text-left py-2">Status</th>
                  <th className="text-left py-2">UTR</th>
                  <th className="text-left py-2 pr-4">Note</th>
                </tr>
              </thead>
              <tbody>
                {q.data!.items.map((p) => (
                  <tr key={p.payout_id} className="border-b border-[color:var(--sg-border)]/60 hover:bg-[color:var(--sg-muted-bg)]/40" data-testid={`payout-row-${p.payout_id}`}>
                    <td className="py-2 pl-4">
                      <div className="font-mono text-[11px] text-[color:var(--sg-fg)]">{p.payout_id}</div>
                      <div className="text-[10px] text-[color:var(--sg-muted)]">
                        {new Date(p.period_start).toLocaleDateString()} – {new Date(p.period_end).toLocaleDateString()}
                      </div>
                    </td>
                    <td className="py-2 text-right font-semibold tabular-nums text-[color:var(--sg-fg)]">{inr(p.amount)}</td>
                    <td className="py-2"><StatusBadge status={p.status}/></td>
                    <td className="py-2 font-mono text-[11px] text-[color:var(--sg-fg)]">{p.utr || "—"}</td>
                    <td className="py-2 pr-4 text-[color:var(--sg-muted)]">{p.note || ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

function inr(n?: number | null) {
  if (n == null) return "—";
  const v = Number(n);
  if (v >= 1e7) return `₹${(v / 1e7).toFixed(1)}Cr`;
  if (v >= 1e5) return `₹${(v / 1e5).toFixed(2)}L`;
  return `₹${v.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
}

function Kpi({ label, value, icon: Icon, tone }: { label: string; value: any; icon: any; tone: string }) {
  const t: Record<string, string> = {
    emerald: "text-emerald-600 dark:text-emerald-300 from-emerald-500/15",
    amber:   "text-amber-600 dark:text-amber-300 from-amber-500/15",
    rose:    "text-rose-600 dark:text-rose-300 from-rose-500/15",
  };
  return (
    <div className="rounded-2xl border border-[color:var(--sg-border)] bg-[color:var(--sg-card)] p-4 relative overflow-hidden">
      <div className={`absolute -top-6 -right-6 h-24 w-24 rounded-full bg-gradient-to-br ${t[tone]} to-transparent blur-2xl`}/>
      <div className="relative">
        <div className={`flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] font-semibold ${t[tone].split(" ")[0]}`}>
          <Icon className="h-3 w-3" strokeWidth={2.5}/> {label}
        </div>
        <div className="mt-1.5 font-serif-display text-2xl font-bold text-[color:var(--sg-fg)] tabular-nums">{value}</div>
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const m: Record<string, string> = {
    paid:   "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200",
    queued: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200",
    held:   "bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-200",
  };
  return <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${m[status] || m.queued}`}>{status}</span>;
}
